import React, { useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
} from '@mui/material';
import { Button } from '@finpay-development/shared-components';
import {
  FacilityWithNpi,
  facilityDisplayName,
} from '../utils/availity-vob';
import '../../../../tab-styling.scss';

export interface SelectFacilityForAvailityDialogProps {
  open: boolean;
  facilities: FacilityWithNpi[];
  isRunning?: boolean;
  onCancel: () => void;
  onConfirm: (facility: FacilityWithNpi) => void;
}

export const SelectFacilityForAvailityDialog: React.FC<
  SelectFacilityForAvailityDialogProps
> = ({ open, facilities, isRunning = false, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState<FacilityWithNpi | null>(null);

  return (
    <Dialog
      open={open}
      onClose={isRunning ? undefined : onCancel}
      maxWidth={false}
      PaperProps={{
        sx: { width: 777, maxWidth: 'calc(100vw - 64px)' },
      }}
      test-id="select-facility-for-availity-dialog"
      TransitionProps={{
        onExited: () => setSelected(null),
      }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        Select Facility for Availity VOB
      </DialogTitle>
      <DialogContent sx={{ overflow: 'visible', pt: '16px !important' }}>
        <Autocomplete
          options={facilities}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          ListboxProps={{ style: { maxHeight: '52vh' } }}
          getOptionLabel={(option) => facilityDisplayName(option)}
          isOptionEqualToValue={(a, b) =>
            Number(a.facilityId) === Number(b.facilityId)
          }
          renderOption={(props, option) => (
            <li {...props} key={option.facilityId}>
              {facilityDisplayName(option)}
              {option.npi ? ` (NPI ${option.npi})` : ''}
            </li>
          )}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Facility"
              placeholder="Search facilities"
              fullWidth
              size="small"
              className="required-fields"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              sx={{
                '& .MuiInputBase-input': {
                  height: '1.4375em !important',
                  paddingTop: '8.5px !important',
                  paddingBottom: '8.5px !important',
                },
              }}
            />
          )}
          test-id="select-facility-for-availity-autocomplete"
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={isRunning}
          test-id="select-facility-for-availity-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (selected) {
              onConfirm(selected);
            }
          }}
          disabled={isRunning || !selected}
          test-id="select-facility-for-availity-run"
        >
          {isRunning ? 'Running...' : 'Run Availity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
